import { Link } from "react-router-dom";
import { FaChartLine, FaCode, FaCloud, FaRocket } from "react-icons/fa6";

const features = [
  {
    icon: <FaChartLine size={24} />,
    title: "AI & Machine Learning",
    desc: "Transform raw data into intelligence. We build robust data pipelines, train predictive ML models, develop custom LLM solutions, and integrate advanced AI capabilities to drive smarter, faster business decisions.",
    color: "from-[#1b667a] to-green-400",
    dotColor: "bg-[#1b667a]",
    ringColor: "border-emerald-200",
    textColor: "text-[#1b667a]",
    url: "/service/ai-machine-learning",
  },
  {
    icon: <FaCode size={24} />,
    title: "Custom Full-Stack Development",
    desc: "From powerful backends to beautiful frontends, we build secure, scalable, and seamless applications using React, Node.js, Python, and modern tech stacks with clean, maintainable architecture.",
    color: "from-blue-500 to-blue-400",
    dotColor: "bg-blue-500",
    ringColor: "border-blue-200",
    textColor: "text-blue-500",
    url: "/service/web-application-development",
  },
  {
    icon: <FaCloud size={24} />,
    title: "DevOps & Cloud Solutions",
    desc: "We help you ship faster and run smarter with cloud-native infrastructure on AWS, Azure, and GCP. Automated CI/CD pipelines, containerization, and reliable DevOps best practices included.",
    color: "from-teal-500 to-cyan-400",
    dotColor: "bg-teal-500",
    ringColor: "border-teal-200",
    textColor: "text-teal-500",
    url: "/service/devops-cloud-solutions",
  },
  {
    icon: <FaRocket size={24} />,
    title: "Digital Transformation Solutions",
    desc: "We modernize legacy systems, automate complex workflows, streamline operations, and unlock new digital opportunities for sustainable, long-term business growth and competitive advantage.",
    color: "from-orange-500 to-amber-400",
    dotColor: "bg-orange-500",
    ringColor: "border-orange-200",
    textColor: "text-orange-500",
    url: "/service/digital-transformation-consulting",
  },
];

export default function EngineeringSection() {
  return (
    <section className="relative py-10 sm:py-12 lg:py-[60px] -mt-6 sm:-mt-10 lg:-mt-40 overflow-hidden bg-gradient-to-b from-gray-50 to-white">
      {/* Decorative background */}
      <div className="absolute inset-0 pointer-events-none mt-10 overflow-hidden">
        <div className="absolute top-[10%] left-[-80px] w-[300px] h-[300px] rounded-full bg-[#1b667a]/[0.03] blur-3xl" />
        <div className="absolute bottom-[10%] right-[-80px] w-[250px] h-[250px] rounded-full bg-blue-500/[0.03] blur-3xl" />
      </div>

      <div className="max-w-[1200px] mx-auto px-4 sm:px-6 relative z-10">
        {/* Heading */}
        <div className="text-center mb-6 mt-6 sm:mt-10 lg:mt-20">
          <h2
            className="text-[52px] leading-[1.15] font-bold text-gray-900 mb-5 max-w-[750px] mx-auto
            max-[1199px]:text-[44px]
            max-[991px]:text-[36px]
            max-[575px]:text-[28px]"
          >
            Engineering Excellence.
            <br />
            Delivering{" "}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#1b667a] to-[#1e9cc3]">
              Real Impact.
            </span>
          </h2>
          <p className="text-gray-500 max-w-[620px] mx-auto leading-relaxed text-[15px] mb-5">
            We combine cutting-edge technology, domain expertise, and a
            relentless focus on quality to build solutions that scale, adapt,
            and drive real impact.
          </p>
          {/* Accent line */}
          <div className="w-[50px] h-[4px] rounded-full bg-gradient-to-r from-[#1b667a] to-emerald-400 mx-auto" />
        </div>

        {/* Connecting dots + Wires */}
        <div className="hidden lg:grid grid-cols-4 max-w-[1200px] mx-auto mt-[50px] relative z-10">
          {/* Horizontal line */}
          <div className="absolute top-[6px] left-[12.5%] right-[12.5%] h-[2px] bg-gradient-to-r from-emerald-300 via-blue-300 to-orange-300 opacity-30" />
          {features.map((f, i) => (
            <div key={i} className="flex flex-col items-center">
              {/* Dot */}
              <div
                className={`relative w-3 h-3 rounded-full ${f.dotColor} ring-4 ring-white z-10 mb-3`}
              />
              {/* Vertical wire */}
              <div
                className={`w-[2px] h-[30px] bg-gradient-to-b ${f.color} opacity-30 rounded-full`}
              />
            </div>
          ))}
        </div>

        {/* Feature Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-[20px] max-w-[1200px] mx-auto">
          {features.map((feature, index) => (
            <div
              key={index}
              className="bg-white rounded-2xl p-5 sm:p-6 border border-gray-100 hover:-translate-y-1 transition-all duration-300 group relative overflow-hidden"
              style={{ boxShadow: "0 4px 24px rgba(0,0,0,0.06)" }}
            >
              {/* Colored top accent */}
              <div
                className={`absolute top-0 left-0 right-0 h-1 bg-gradient-to-r ${feature.color}`}
              />
              {/* Colored background glow */}
              <div
                className={`absolute -top-10 -right-10 w-24 h-24 rounded-full bg-gradient-to-br ${feature.color} opacity-[0.06] blur-xl`}
              />
              {/* Icon */}
              <div
                className={`w-[50px] h-[50px] rounded-xl bg-gradient-to-br ${feature.color} flex items-center justify-center text-white mb-4 shadow-[0_4px_12px_rgba(0,0,0,0.12)]`}
              >
                {feature.icon}
              </div>
              {/* Title */}
              <h3 className="text-[19px] font-bold text-gray-900 mb-2.5 leading-tight">
                {feature.title}
              </h3>
              {/* Description */}
              <p className="text-[15px] text-gray-500 leading-[1.7]">
                {feature.desc}
              </p>
              {/* Link */}
              <Link
                to={feature.url}
                className={`inline-flex items-center gap-1.5 text-[14px] font-semibold ${feature.textColor} group-hover:gap-2.5 transition-all duration-300 mt-4`}
              >
                Explore Solutions
                <svg
                  width="12"
                  height="12"
                  viewBox="0 0 16 16"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M3 8H13M13 8L9 4M13 8L9 12"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </Link>
            </div>
          ))}
        </div>
      </div>

      {/* Decorative 3D shapes */}
      {/* Left sphere */}
      <div className="hidden xl:block absolute left-[1%] top-[25%] z-0">
        <div className="relative w-[100px] h-[100px]">
          <div className="absolute inset-0 rounded-full bg-gradient-to-br from-emerald-300/30 to-teal-400/20 blur-[2px]" />
          <div className="absolute inset-1 rounded-full bg-gradient-to-br from-emerald-200/40 to-teal-300/20 shadow-lg" />
        </div>
      </div>
      {/* Right ring */}
      <div className="hidden xl:block absolute right-[1%] top-[15%] z-0">
        <div className="relative w-[180px] h-[180px] opacity-50">
          <div className="absolute inset-0 rounded-full border-[16px] border-blue-200/30" />
          <div className="absolute inset-4 rounded-full border-[10px] border-teal-200/20" />
          <div className="absolute inset-8 rounded-full border-[6px] border-emerald-100/20" />
        </div>
      </div>
    </section>
  );
}
